"use client";

import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import ConsentIntroStep from '@/features/consent/components/consent-intro-step';
import ConsentRequisitesForm from '@/features/consent/components/consent-requisites-form';
import ConsentSignStep from '@/features/consent/components/consent-sign-step';
import ConsentDoneStep from '@/features/consent/components/consent-done-step';
import type { TConsentStatus } from '@/features/consent/types';

export type TConsentStep = 'intro' | 'requisites' | 'sign' | 'done';

const STEP_BY_STATUS: Record<TConsentStatus, TConsentStep> = {
  not_started: 'intro',
  requisites_filled: 'sign',
  pending_signature: 'sign',
  signed: 'done',
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status?: TConsentStatus;
  // «Перейти к загрузке фото» в окне 4.
  onSigned: () => void;
}

// Четыре окна сценария — один диалог с внутренним шагом: переход между окнами не
// должен размонтировать контейнер, иначе на каждом шаге моргает оверлей.
export default function ConsentDialog({ open, onOpenChange, status = 'not_started', onSigned }: Props)
{
  const [currentStatus, setCurrentStatus] = useState<TConsentStatus>(status);
  const [step, setStep] = useState<TConsentStep>(STEP_BY_STATUS[status]);

  // Диалог живёт всё время, пока смонтирован родитель, поэтому каждое открытие
  // начинается заново — с того окна, которое отвечает текущему статусу.
  useEffect(() => {
    if (open) {
      setCurrentStatus(status);
      setStep(STEP_BY_STATUS[status]);
    }
  }, [open, status]);

  const handleRequisitesSaved = (nextStatus: TConsentStatus) => {
    setCurrentStatus(nextStatus);
    setStep('sign');
  }

  const handleSignedInBackground = () => {
    setCurrentStatus('signed');
    setStep('done');
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={step === 'requisites' ? 'sm:max-w-[760px]' : 'sm:max-w-[560px]'}>
        {step === 'intro' && (
          <ConsentIntroStep onContinue={() => setStep('requisites')} />
        )}

        {step === 'requisites' && (
          <ConsentRequisitesForm onSaved={handleRequisitesSaved} />
        )}

        {step === 'sign' && (
          <ConsentSignStep
            isPendingSignature={currentStatus === 'pending_signature'}
            onEditRequisites={() => setStep('requisites')}
            onAlreadySigned={handleSignedInBackground}
          />
        )}

        {step === 'done' && (
          <ConsentDoneStep onContinue={onSigned} />
        )}
      </DialogContent>
    </Dialog>
  );
}
